import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { getMe, logout as apiLogout, createAuthSession } from "@/lib/api";
import { trackEvent } from "@/lib/useAnalytics";

const AuthContext = createContext(null);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const EMERGENT_AUTH_URL = "https://auth.emergentagent.com/";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const processedSession = useRef(false);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const data = await getMe();
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Handle OAuth callback - process session_id from URL hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash?.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) {
        // Guard against React StrictMode double-invocation (session_id is single-use)
        if (processedSession.current) return;
        processedSession.current = true;
        // Remove hash from URL
        window.history.replaceState({}, "", window.location.pathname + window.location.search);

        createAuthSession(sessionId)
          .then((data) => {
            setUser(data);
            setLoading(false);
            // Track auth event
            const isNewUser = data?.created_at && (Date.now() - new Date(data.created_at).getTime() < 60000);
            trackEvent(isNewUser ? "signup_success" : "login_success", { user_id: data?.email });
          })
          .catch(() => {
            setLoading(false);
          });
      }
    }
  }, []);

  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href = `${EMERGENT_AUTH_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
      trackEvent("logout");
    } catch {}
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, loginWithGoogle, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
